import type React from "react";
import {observer} from "mobx-react-lite";
import {Chip, Stack} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import {authStore, notificationStore} from "~/stores/init";
import type {SupplierSuggestion} from "~/pages/supplier-360/types/Supplier360PdlTypes";
import {useDecideSuggestionMutation} from "~/pages/supplier-360/tabs/SupplierOverview/hooks/useDecideSuggestionMutation";

/**
 * Alternative names not yet in the cluster, as dashed chips under Aliases: the check accepts, the
 * cross dismisses — a dismissed name is never suggested again for this supplier.
 */
export const AliasSuggestions: React.FC<{suggestions: SupplierSuggestion[]}> = observer(({suggestions}) => {
    const {mutate, isPending} = useDecideSuggestionMutation();
    const canEdit = authStore.hasEditPermission;
    if (suggestions.length === 0) return null;

    const decide = (suggestion: SupplierSuggestion, decision: 'accept' | 'dismiss') => mutate({suggestion, decision}, {
        onError: () => notificationStore.show('Could not save that decision. Please try again.', {
            severity: 'error', id: `supplier-suggestion-error-${suggestion.id}`, autoHideDuration: 3000,
        }),
    });

    return (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {suggestions.map(s => (
                <Chip
                    key={s.id}
                    label={s.value}
                    size="small"
                    variant="outlined"
                    title="Suggested alias"
                    sx={{borderStyle: 'dashed'}}
                    disabled={isPending}
                    icon={canEdit ? <CheckIcon aria-label={`Accept alias ${s.value}`} onClick={() => decide(s, 'accept')}
                                               sx={{cursor: 'pointer'}}/> : undefined}
                    onDelete={canEdit ? () => decide(s, 'dismiss') : undefined}
                />
            ))}
        </Stack>
    );
});
